import { Paper, Stack, Typography, Button, Box, Tabs, Tab, Grid, Switch, FormControlLabel, Alert, CircularProgress } from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PaymentIcon from "@mui/icons-material/Payment";
import SaveIcon from "@mui/icons-material/Save";
import { useState } from "react";
import { EnhancedMembershipsList } from "../../components/memberships-list/enhanced-memberships-list";
import { PlanAdminPanel } from "../../components/memberships-list/plan-admin-panel";
import { PlanConfiguration } from "../../components/memberships-list/plan-configuration";
import { usePaymentSettings } from "../../contexts/PaymentSettingsContext";
import { useUpdatePaymentSettingsMutation } from "../../hooks/useUpdatePaymentSettingsMutation";
import { useNotification } from "../../contexts/NotificationContext";

export const AdminMembershipPlansPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [planConfigurations, setPlanConfigurations] = useState<PlanConfiguration[]>([]);
  const { settings, updateSettings, isPaymentEnabled, isTestMode, hasUnsavedChanges, isLoading } = usePaymentSettings();
  const { updatePaymentSettings, loading: saving } = useUpdatePaymentSettingsMutation();
  const { showSuccess, showError } = useNotification();

  const handleSaveSettings = async () => {
    try {
      await updatePaymentSettings({
        paymentEnabled: settings.paymentEnabled,
        testMode: settings.testMode,
        testModeMessage: settings.testModeMessage,
      });
      // The mutation automatically refetches via refetchQueries
      showSuccess("Payment settings saved successfully");
    } catch (error) {
      showError("Failed to save payment settings. Please try again.");
    }
  };

  const handleConfigurationChange = (configurations: PlanConfiguration[]) => {
    setPlanConfigurations(configurations);
  };

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 4,
          border: (theme) => `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: "#fcfdff",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <CreditCardOutlinedIcon sx={{ fontSize: 32, color: "secondary.main" }} />
              <Typography variant="h4" fontWeight={700}>
                Membership Plans
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Manage pricing tiers, subscription options, and plan configurations
            </Typography>
          </Stack>
          
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => setShowAdminPanel(true)}
            sx={{
              backgroundColor: "#d22268",
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "#b01d56",
              },
            }}
          >
            Configure Plans
          </Button>
        </Stack>

        {/* Payment Settings Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: (theme) => `1px solid ${theme.palette.grey[200]}`,
            backgroundColor: "#fafafa",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <PaymentIcon sx={{ fontSize: 24, color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Payment Processing Settings
              </Typography>
            </Stack>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSaveSettings}
              disabled={!hasUnsavedChanges || saving || isLoading}
              sx={{
                backgroundColor: "#5C3BFF",
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                "&:hover": {
                  backgroundColor: "#4a2fd9",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#e0e0e0",
                  color: "#9e9e9e",
                },
              }}
            >
              {saving ? "Saving..." : "Save Settings"}
            </Button>
          </Stack>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.paymentEnabled}
                    onChange={(e) => updateSettings({ paymentEnabled: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      Enable Payment Processing
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Allow users to make real payments for memberships
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.testMode}
                    onChange={(e) => updateSettings({ testMode: e.target.checked })}
                    color="primary"
                    disabled={!settings.paymentEnabled}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      Test Mode
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Show test mode banner and simulate payments
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>

          {/* Status Alert */}
          <Alert 
            severity={settings.paymentEnabled ? (settings.testMode ? "warning" : "success") : "info"}
            sx={{ mt: 2 }}
          >
            <Typography variant="body2">
              <strong>Current Status:</strong> {
                !settings.paymentEnabled 
                  ? "Payments disabled - Users will see test mode"
                  : settings.testMode 
                    ? "Test mode enabled - No real charges will be processed"
                    : "Live payments enabled - Real charges will be processed"
              }
            </Typography>
          </Alert>
        </Paper>

        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="Homeowner Plans" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Designer Plans" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Homeowner Membership Plans
            </Typography>
            <EnhancedMembershipsList
              variant="homeowner"
              showBetaPlans={true}
              showPlaceholders={true}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Designer Membership Plans
            </Typography>
            <EnhancedMembershipsList
              variant="designer"
              showBetaPlans={true}
              showPlaceholders={true}
            />
          </Box>
        )}
      </Paper>

      {/* Plan Configuration Modal */}
      <PlanAdminPanel
        open={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
        onConfigurationChange={handleConfigurationChange}
      />
    </Box>
  );
};

export default AdminMembershipPlansPage;
